// src/pages/Informe30Dias.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ENDPOINT =
  "https://marketbusters.pro/informes/get_productos_fecha_personalizada.php";

export default function Informe30Dias() {
  const [fecha, setFecha] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [selProv, setSelProv] = useState(new Set());

  /* ─── Descarga y transformación ─── */
  useEffect(() => {
    if (!fecha) {
      setRows([]);
      setProveedores([]);
      setSelProv(new Set());
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${ENDPOINT}?fecha=${encodeURIComponent(fecha)}`
        );
        const transformed = transformar(data || []);
        setRows(transformed);

        const únicos = Array.from(
          new Set(transformed.map((r) => r.Proveedor))
        ).sort();

        setProveedores(únicos);
        setSelProv(new Set(únicos)); // todos activos al inicio
      } catch (e) {
        console.error(e);
        setRows([]);
        setProveedores([]);
        setSelProv(new Set());
      } finally {
        setLoading(false);
      }
    })();
  }, [fecha]);

  /* ─── Convertir a variaciones + totales ─── */
  const transformar = (arr) =>
    arr
      .filter((p) => p["País"] === "Chile")
      .map((p) => {
        const fechas = Object.keys(p).filter((k) => /^\d{4}-\d{2}-\d{2}/.test(k));
        const variaciones = {};
        let totalNeg = 0;
        let diasNeg = 0;

        fechas.sort().forEach((f, i) => {
          const cur = parseFloat(p[f] === "-" ? NaN : p[f]);
          const prev =
            i === 0
              ? NaN
              : parseFloat(p[fechas[i - 1]] === "-" ? NaN : p[fechas[i - 1]]);
          const diff =
            !isNaN(cur) && !isNaN(prev) ? cur - prev : null;

          variaciones[f] = diff;
          if (diff !== null && diff < 0) {
            totalNeg += diff;
            diasNeg += 1;
          }
        });

        return {
          ID: p.ID,
          Nombre: p.Nombre,
          Proveedor: p.Proveedor,
          variaciones,
          Total: totalNeg,
          DiasNeg: diasNeg,
        };
      })
      .filter((r) => r.Total < 0 && r.Total > -999) // -999 < Total < 0
      .sort((a, b) => b.DiasNeg - a.DiasNeg);       // desc por días neg

  const colFechas =
    rows.length > 0 ? Object.keys(rows[0].variaciones).sort() : [];

  /* ─── Filtro de proveedor ─── */
  const visibles = rows.filter((r) => selProv.has(r.Proveedor));
  const toggleProv = (prov) =>
    setSelProv((prev) => {
      const next = new Set(prev);
      next.has(prov) ? next.delete(prov) : next.add(prov);
      return next;
    });

  /* ─── Exportar a Excel ─── */
  const exportarXLSX = () => {
    if (visibles.length === 0) return;

    const header = [
      "ID",
      "Producto",
      "Proveedor",
      ...colFechas,
      "Total",
      "DiasNeg",
    ];
    const body = visibles.map((r) => [
      r.ID,
      r.Nombre,
      r.Proveedor,
      ...colFechas.map((f) => r.variaciones[f]),
      r.Total,
      r.DiasNeg,
    ]);

    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Informe");

    const start = fecha;
    const end = new Date(fecha);
    end.setDate(end.getDate() + 30);
    const endStr = end.toISOString().slice(0, 10);

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `DROPDROP_${start}_a_${endStr}.xlsx`
    );
  };

  /* ─── UI ─── */
  return (
    <div className="min-h-screen bg-zinc-900 text-zinc-100 p-6">
      <h1 className="text-3xl font-bold mb-8">
        Informe – Variaciones negativas (Chile)
      </h1>

      {/* Calendario + botón + proveedores */}
      <div className="flex flex-wrap items-start gap-4 mb-6">
        {/* Fecha y botón juntos */}
        <div className="flex items-center gap-4">
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="w-max bg-zinc-800 border border-zinc-700 px-3 py-2 rounded-md"
          />
          <button
            onClick={exportarXLSX}
            disabled={visibles.length === 0}
            className="w-max bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md disabled:opacity-40"
          >
            📥 Exportar XLSX
          </button>
        </div>

        {/* Lista de proveedores */}
        {proveedores.length > 0 && (
          <div className="flex flex-wrap gap-3 bg-zinc-800 p-3 rounded-lg max-h-40 overflow-auto">
            {proveedores.map((prov) => (
              <label key={prov} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selProv.has(prov)}
                  onChange={() => toggleProv(prov)}
                  className="accent-blue-600"
                />
                {prov}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Loader / Tabla */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin h-10 w-10 rounded-full border-b-2 border-blue-500" />
        </div>
      ) : visibles.length === 0 ? (
        <p className="text-center text-zinc-400">
          {fecha ? "Sin datos para los filtros seleccionados." : "Selecciona una fecha."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg shadow ring-1 ring-zinc-700">
          <table className="min-w-max w-full text-sm">
            <thead className="bg-zinc-800 sticky top-0">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Producto</th>
                <th className="px-4 py-3">Proveedor</th>
                {colFechas.map((f) => (
                  <th key={f} className="px-4 py-3">
                    {f}
                  </th>
                ))}
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">DiasNeg</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((row, i) => (
                <tr
                  key={`${row.ID}-${i}`}
                  className="even:bg-zinc-800 odd:bg-zinc-900 hover:bg-zinc-700"
                >
                  <td className="px-4 py-2">{row.ID}</td>
                  <td className="px-4 py-2">{row.Nombre}</td>
                  <td className="px-4 py-2">{row.Proveedor}</td>
                  {colFechas.map((f) => {
                    const d = row.variaciones[f];
                    return (
                      <td
                        key={f}
                        className={`px-4 py-2 ${
                          d === null
                            ? "text-zinc-500"
                            : d < 0
                            ? "text-green-400 font-medium"
                            : d > 0
                            ? "text-red-400 font-medium"
                            : "text-zinc-300"
                        }`}
                      >
                        {d === null ? "—" : d}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2 font-bold text-green-400">
                    {row.Total}
                  </td>
                  <td className="px-4 py-2 font-bold">{row.DiasNeg}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
